import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SearchableSelect from "../common/SearchableSelect";
import "./HierarchyFilterBar.css";
import "./new_HierarchyFilterBar.css";

export default function HierarchyFilterBar({
  allDatesOptionValue = "all",
  className = "",
  compact = false,
  districtDisabled = false,
  hideDateFilterLabel = false,
  districtOptions = [],
  selectedDistrictLabel = "",
  selectedStationLabel = "",
  selectedSubDivisionLabel = "",
  stationOptions = [],
  stationDisabled = false,
  subDivisionOptions = [],
  subDivisionDisabled = false,
  userOptions = [],
  showDistrictFilter = true,
  showStationFilter = true,
  showSubDivisionFilter = true,
  showUserFilter = false,
  selectedDistrictId = "",
  selectedEndDate = null,
  selectedFilterOption = "",
  selectedStartDate = null,
  selectedStationId = "",
  selectedSubDivisionId = "",
  selectedUserId = "",
  onDateRangeChange,
  onDistrictChange,
  onFilterOptionChange,
  onReset,
  onStationChange,
  onSubDivisionChange,
  onUserChange,
  quickFilterOptions = [],
  showDateFilters = false,
  showReset = false,
  translate,
}) {
  const text = (key, fallback) => {
    const translatedValue = translate?.(key);
    return translatedValue && translatedValue !== key
      ? translatedValue
      : fallback;
  };
  const hasVisibleControls =
    showDistrictFilter ||
    showSubDivisionFilter ||
    showStationFilter ||
    showUserFilter ||
    showDateFilters ||
    showReset;

  if (!hasVisibleControls) {
    return null;
  }

  const showCustomRange = showDateFilters && selectedFilterOption === "custom";
  const useInlineReset = showDateFilters && showReset;
  const lockedTooltipText = text("not_accessible", "Not accessible");
  const getLockedLabel = (selectedLabel, selectedValue, fallbackLabel) =>
    selectedLabel || selectedValue || fallbackLabel;
  const hasSelectedOption = (items, selectedValue) =>
    items.some((item) => String(item?.id ?? "") === String(selectedValue ?? ""));
  const renderSelectWrapper = (control, disabled) =>
    disabled ? (
      <div
        className="report-filter-tooltip"
        data-tooltip={lockedTooltipText}
        title={lockedTooltipText}
      >
        {control}
      </div>
    ) : (
      control
    );

  const resetButton = showReset ? (
    <button className="report-filter-reset" onClick={onReset} type="button">
      {text("reset", "Reset")}
    </button>
  ) : null;

  return (
    <div
      className={`report-filter-bar${
        compact ? " report-filter-bar--compact" : ""
      }${showCustomRange ? " report-filter-bar--custom-range" : ""}${
        className ? ` ${className}` : ""
      }`}
    >
      <div className="report-filter-grid">
        {showDistrictFilter && !districtDisabled ? (
          <div className="report-filter-field">
            <label className="report-filter-label" htmlFor="report-district-filter">
              {text("district", "District")}
            </label>
            {renderSelectWrapper(
              <SearchableSelect
                className={`report-filter-control${
                  districtDisabled ? " report-filter-control--disabled" : ""
                }`}
                disabled={districtDisabled}
                id="report-district-filter"
                onChange={onDistrictChange}
                value={selectedDistrictId}
                searchPlaceholder={text("search_district", "Search district")}
              >
                {districtDisabled && !hasSelectedOption(districtOptions, selectedDistrictId) ? (
                  <option value={selectedDistrictId}>
                    {getLockedLabel(
                      selectedDistrictLabel,
                      selectedDistrictId,
                      text("assigned_district", "Assigned District")
                    )}
                  </option>
                ) : (
                  <option value="">
                    {text("all_districts", "All Districts")}
                  </option>
                )}
                {districtOptions.map((district) => (
                  <option key={district.id} value={district.id}>
                    {district.name || district.label || district.id}
                  </option>
                ))}
              </SearchableSelect>,
              districtDisabled
            )}
          </div>
        ) : null}

        {showSubDivisionFilter && !subDivisionDisabled ? (
          <div className="report-filter-field">
            <label
              className="report-filter-label"
              htmlFor="report-subdivision-filter"
            >
              {text("sub_division", "Sub-division")}
            </label>
            {renderSelectWrapper(
              <SearchableSelect
                className={`report-filter-control${
                  subDivisionDisabled ? " report-filter-control--disabled" : ""
                }`}
                disabled={subDivisionDisabled}
                id="report-subdivision-filter"
                onChange={onSubDivisionChange}
                value={selectedSubDivisionId}
                searchPlaceholder={text(
                  "search_sub_division",
                  "Search sub-division"
                )}
              >
                {subDivisionDisabled &&
                !hasSelectedOption(subDivisionOptions, selectedSubDivisionId) ? (
                  <option value={selectedSubDivisionId}>
                    {getLockedLabel(
                      selectedSubDivisionLabel,
                      selectedSubDivisionId,
                      text(
                        "assigned_sub_division",
                        "Assigned Sub-division"
                      )
                    )}
                  </option>
                ) : (
                  <option value="">
                    {text("all_sub_divisions", "All Sub-divisions")}
                  </option>
                )}
                {subDivisionOptions.map((subDivision) => (
                  <option key={subDivision.id} value={subDivision.id}>
                    {subDivision.name || subDivision.label || subDivision.id}
                  </option>
                ))}
              </SearchableSelect>,
              subDivisionDisabled
            )}
          </div>
        ) : null}

        {showStationFilter && !stationDisabled ? (
          <div className="report-filter-field">
            <label className="report-filter-label" htmlFor="report-station-filter">
              {text("police_station", "Police Station")}
            </label>
            {renderSelectWrapper(
              <SearchableSelect
                className={`report-filter-control${
                  stationDisabled ? " report-filter-control--disabled" : ""
                }`}
                disabled={stationDisabled}
                id="report-station-filter"
                onChange={onStationChange}
                value={selectedStationId}
                searchPlaceholder={text(
                  "search_police_station",
                  "Search police station"
                )}
              >
                {stationDisabled && !hasSelectedOption(stationOptions, selectedStationId) ? (
                  <option value={selectedStationId}>
                    {getLockedLabel(
                      selectedStationLabel,
                      selectedStationId,
                      text(
                        "assigned_police_station",
                        "Assigned Police Station"
                      )
                    )}
                  </option>
                ) : (
                  <option value="">
                    {text("all_police_stations", "All Police Stations")}
                  </option>
                )}
                {stationOptions.map((station) => (
                  <option key={station.id} value={station.id}>
                    {station.name || station.label || station.id}
                  </option>
                ))}
              </SearchableSelect>,
              stationDisabled
            )}
          </div>
        ) : null}

        {showUserFilter ? (
          <div className="report-filter-field">
            <label className="report-filter-label" htmlFor="report-user-filter">
              {text("user", "User")}
            </label>
            <SearchableSelect
              className="report-filter-control"
              id="report-user-filter"
              onChange={onUserChange}
              value={selectedUserId}
              searchPlaceholder={text("search_user", "Search user")}
            >
              <option value="">{text("all_users", "All Users")}</option>
              {userOptions.map((user) => (
                <option
                  key={user.id || user.user_id || user.value}
                  value={user.id || user.user_id || user.value}
                >
                  {user.name ||
                    user.user_name ||
                    user.label ||
                    user.username ||
                    user.email ||
                    user.id ||
                    user.user_id}
                </option>
              ))}
            </SearchableSelect>
          </div>
        ) : null}

        {showDateFilters ? (
          <div
            className={`report-filter-field${
              useInlineReset && !showCustomRange && showReset
                ? " report-filter-field--inline-reset"
              : ""
            }`}
          >
            {!hideDateFilterLabel ? (
              <label className="report-filter-label" htmlFor="report-time-filter">
                {text("date_filter", "Date Filter")}
              </label>
            ) : null}
            <div className="report-filter-inline-control">
              <SearchableSelect
                className="report-filter-control"
                id="report-time-filter"
                onChange={onFilterOptionChange}
                value={selectedFilterOption}
                searchPlaceholder={text(
                  "search_date_filter",
                  "Search date filter"
                )}
              >
                <option value={allDatesOptionValue}>
                  {text("all_dates", "All Dates")}
                </option>
                {quickFilterOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </SearchableSelect>
              {useInlineReset && !showCustomRange ? resetButton : null}
            </div>
          </div>
        ) : null}

        {showCustomRange ? (
          <div
            className={`report-filter-field report-filter-field--range${
              useInlineReset && showReset
                ? " report-filter-field--inline-reset"
                : ""
            }`}
          >
            <label className="report-filter-label">
              {text("custom_range", "Custom Range")}
            </label>
            <div className="report-filter-inline-control">
              <DatePicker
                className="report-filter-control"
                dateFormat="dd/MM/yyyy"
                endDate={selectedEndDate}
                onChange={onDateRangeChange}
                placeholderText={text(
                  "select_date_range",
                  "Select date range"
                )}
                selectsRange
                startDate={selectedStartDate}
              />
              {useInlineReset ? resetButton : null}
            </div>
          </div>
        ) : null}

        {showReset && !useInlineReset ? (
          <div className="report-filter-actions">
            {resetButton}
          </div>
        ) : null}
      </div>
    </div>
  );
}
